import * as yup from 'yup';
import { QUESTION_TYPES_MAP } from 'utils/constants';

const wysiwygInputSchema = yup
  .object()
  .shape({
    html: yup.string(),
    spacedPinyin: yup.array().of(
      yup.object().shape({
        index: yup.number(),
        span: yup.number(),
        pinyin: yup.string(),
      })
    ),
    audioFileKey: yup.string(),
  })
  .default(undefined);

const questionComplexContentInputSchema = yup
  .object()
  .shape({
    type: yup.string(),
    text: wysiwygInputSchema,
    imageFileKey: yup.string(),
    gifFileKey: yup.string(),
    audioFileKey: yup.string(),
  })
  .default(undefined);

const questionOptionComplexContentInputSchema = yup
  .object()
  .shape({
    type: yup.string(),
    text: wysiwygInputSchema,
    imageFileKey: yup.string(),
    gifFileKey: yup.string(),
    audioFileKey: yup.string(),
    liveFeedback: yup.string().nullable(),
  })
  .default(undefined);

const childQuestionInputSchema = yup
  .object()
  .shape({
    _id: yup.string(),
    title: yup.string(),
    correctOptions: yup.array().of(yup.number()),
    tableValues: yup.array().of(yup.string()),
    answers: yup.array().of(
      yup.object().shape({
        _id: yup.string(),
        answer: yup.string(),
        contentTagIds: yup.array().of(yup.string()).nullable(),
        scopeTagIds: yup.array().of(yup.string()).nullable(),
        languageConceptTagIds: yup.array().of(yup.string()).nullable(),
        focusTagsIds: yup.array().of(yup.string()).nullable(),
        responseFormatTagId: yup.string().nullable(),
        expressionMethodTagIds: yup.array().of(yup.string()).nullable(),
        meeoppLevelId: yup.string().nullable(),
        coreWordIds: yup.array().of(yup.string()).nullable(),
        autoGraderKeywords: yup.array().of(yup.string()),
        sampleAnswers: yup.array().of(yup.string()),
      })
    ),
    contentTagIds: yup.array().of(yup.string()).nullable(),
    scopeTagIds: yup.array().of(yup.string()).nullable(),
    languageConceptTagIds: yup.array().of(yup.string()).nullable(),
    focusTagsIds: yup.array().of(yup.string()).nullable(),
    responseFormatTagId: yup.string().nullable(),
    expressionMethodTagIds: yup.array().of(yup.string()).nullable(),
    meeoppLevelId: yup.string().nullable(),
    coreWordIds: yup.array().of(yup.string()).nullable(),
  })
  .default(undefined);

const baseSchema = yup.object().shape({
  _id: yup.string(),
  answerExplanation: yup.string(),
  answerExplanationWithPinyin: wysiwygInputSchema,
  autoGraderKeywords: yup.array().of(yup.string()),
  hint: yup
    .object()
    .shape({
      content: yup.string(),
      url: yup.string(),
      type: yup.string(),
    })
    .default(undefined),
  media: yup
    .object()
    .shape({
      url: yup.string(),
      type: yup.string(),
      transcript: yup.string().nullable(),
    })
    .default(undefined),
  order: yup.number(),
  questionData: yup
    .object()
    .shape({
      options: yup.array().of(yup.string()),
      optionAudioClips: yup.array().of(yup.string().nullable()),
      correctOptions: yup.array().of(yup.number()),
      optionsComplex: yup.array().of(questionOptionComplexContentInputSchema),
      tableHeaders: yup.array().of(yup.string()),
      tableValues: yup.array().of(yup.string()),
      questionHeader: yup.string(),
      answerHeader: yup.string(),
      answerHeaders: yup.array().of(yup.string()),
      exampleOption: yup.number(),
      shortAnswerType: yup.string(),
      subTypeMeta: yup.object().shape({
        isCorrect: yup.bool(),
      }),
      childQuestions: yup.array().of(childQuestionInputSchema),
    })
    .default(undefined),
  questionType: yup.string().oneOf(Object.values(QUESTION_TYPES_MAP)),
  tags: yup.array().of(yup.string()),
  contentTagIds: yup.array().of(yup.string()).nullable(),
  scopeTagIds: yup.array().of(yup.string()).nullable(),
  languageConceptTagIds: yup.array().of(yup.string()).nullable(),
  focusTagsIds: yup.array().of(yup.string()).nullable(),
  responseFormatTagId: yup.string().nullable(),
  expressionMethodTagIds: yup.array().of(yup.string()).nullable(),
  title: yup.string(),
  example: yup
    .object()
    .shape({
      tableValues: yup.array().of(yup.string()).nullable(),
      title: yup.string(),
      answer: yup.string(),
      answers: yup.array().of(yup.string()),
    })
    .default(undefined),
  instructorNote: yup.string(),
  titleComplex: questionComplexContentInputSchema,
  learningSubject: yup.string(),
  meeoppLevelId: yup.string().nullable(),
  coreWordIds: yup.array().of(yup.string()).nullable(),
  // should also include one of
  topicId: yup.string(),
  quizId: yup.string(),
  presentationId: yup.string(),
  playbookId: yup.string(),
  publishedPlaybookId: yup.string(),
  slideIndex: yup.number(),
  questionSet: yup.string(),
  optionAudioClips: yup.string(),
});

export default baseSchema;
